import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Checkbox, Input } from 'antd';
import { AxiosResponse } from 'axios';
import { TbArrowUpRight, TbInfoCircle, TbMailShare, TbSearch } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { PlanTag } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { EMAIL_LINKS } from '@/shared/constants/externalLinks';

interface CompanyTagsProps {
    filters: any;
    handleChange: any;
}

const CompanyTags: FC<CompanyTagsProps> = ({ filters, handleChange }): JSX.Element => {
    const [tags, setTags] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const { hasMinimumPlan } = useEntitlements();
    const { token } = useAntDesignTheme();

    const handleGetTechnologies = async () => {
        try {
            setLoading(true);

            await authorizedAxiosInstance
                .get(`/api/vendors/clearbit/company-tags`)
                .then((res: AxiosResponse) => {
                    if (res?.status === 200) {
                        setTags(res?.data);

                        setLoading(false);
                    }
                })
                .finally(() => setLoading(false));
        } catch (error) {
            captureException(error);
        }
    };

    useEffect(() => {
        if (hasMinimumPlan('plus')) {
            handleGetTechnologies();
        }
    }, []);

    const [searchText, setSearchText] = useState('');

    // Filter by key or value
    const filteredTags = tags.filter((key: string) => key.toLowerCase().includes(searchText.toLowerCase()));

    return !hasMinimumPlan('plus') ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>
            <PlanTag plan={'plus'} style={{ marginLeft: '0.5rem' }} />
        </div>
    ) : (
        <>
            <PlatformAlert
                title='Enriched company attributes'
                showIcon
                status='info'
                icon={
                    <TbInfoCircle
                        size='1.125rem'
                        style={{
                            marginRight: '0.75rem',
                            marginTop: '0.1rem',
                        }}
                    />
                }
                description={
                    <span>
                        Filters for popular tags associated with certain companies. We use{' '}
                        <a href='http://clearbit.com' target='_blank' rel='noopener noreferrer'>
                            Clearbit
                            <TbArrowUpRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.125rem' }} />
                        </a>{' '}
                        to enrich data on Countercyclical. If you see any errors, please reach out to{' '}
                        <a href={EMAIL_LINKS.engineering}>
                            us
                            <TbMailShare style={{ verticalAlign: '-0.2rem', marginLeft: '0.25rem' }} />
                        </a>
                    </span>
                }
                style={{ marginBottom: '0.75rem' }}
            />
            <Input
                placeholder='Search...'
                suffix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                value={searchText}
                onChange={(e: any) => setSearchText(e.target.value)}
            />
            {filteredTags?.length !== 0 && !loading ? (
                <>
                    <div style={{ margin: '0.5rem 0' }}>
                        <small>
                            <span style={{ color: token.colorTextTertiary }}>
                                {filteredTags?.length?.toLocaleString()} Attributes
                            </span>
                        </small>
                    </div>
                    <Checkbox.Group
                        value={filters?.attributes}
                        onChange={(checkedValues) => {
                            handleChange(['attributes'], checkedValues);
                        }}
                    >
                        {filteredTags?.map((e: string) => {
                            return (
                                <div style={{ marginBottom: '0.125rem', width: '100%' }}>
                                    <Checkbox value={e} />
                                    <span style={{ marginLeft: '0.5rem' }}>{e}</span>
                                </div>
                            );
                        })}
                    </Checkbox.Group>
                </>
            ) : (
                loading && (
                    <div style={{ marginTop: '1rem' }}>
                        <BaseSkeleton active round paragraph={{ rows: 7 }} />
                    </div>
                )
            )}
        </>
    );
};

export default CompanyTags;
